import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Alert, AlertIcon, AlertDescription, Spinner, Center } from '@chakra-ui/react';
import { useHandleRubricAnswerChange, spanSelectors } from '@codametrix/ui-common';
import {
  codeTypeToEntityType,
  getQuestionIdsToClear,
  isCodeComplete,
} from '../highlight-drawer/rubric-utils';
import { useRubricAutofill } from '../highlight-drawer/useRubricAutofill';
import {
  useWorkflowEvaluation,
  EvaluateWorkflowFn,
} from '../highlight-drawer/useWorkflowEvaluation';
import WorkflowWizard from './workflow-wizard';
import './styles/index.scss';
export interface CodeQuestionsSectionProps {
  /** The CQA code object containing id, codeType, and attributes */
  code: Accelerate.CQACode;
  /** The workflow for this code type */
  rubricWorkflow: CQA.CodeWorkflow;
  /** Callback for attribute updates */
  onAnswerChange: (params: CQA.UpdateAttributeParams) => void;
  /** Callback for attribute deletions */
  onDeleteAnswer: (params: CQA.DeleteAttributeParams) => Promise<void>;
  /** Callback for optimistic batch clearing of downstream attributes */
  onClearDownstream?: (params: CQA.ClearDownstreamAttributesParams) => void;
  /** Function to call the workflow evaluation API */
  evaluateFn?: EvaluateWorkflowFn;
  /** Work item ID (t_work_item.id) for system question expression evaluation */
  workItemId?: number;
  /** Optional disabled state */
  isDisabled?: boolean;
  /** When true, show an info banner that case-level questions must be completed first */
  isCaseIncomplete?: boolean;
  /**
   * Called when the code's roll-up completion changes (all required visible
   * questions answered). Fired only once evaluation has settled (not loading).
   */
  onCompletionChange?: (complete: boolean) => void;
  /** Ref that receives the wizard's reset function so the code row can place the button externally. */
  resetRef?: React.MutableRefObject<(() => void) | null>;
}

/**
 * CodeQuestionsSection renders rubric questions for a specific code.
 * It uses the workflow for the code's type (PROCEDURE, MODIFIER, DIAGNOSIS)
 * and displays questions with trigger-based sequencing.
 */
export const CodeQuestionsSection: React.FC<CodeQuestionsSectionProps> = ({
  code,
  rubricWorkflow,
  onAnswerChange,
  onDeleteAnswer,
  onClearDownstream,
  evaluateFn,
  workItemId,
  isDisabled = false,
  isCaseIncomplete = false,
  onCompletionChange,
  resetRef,
}) => {
  const attributes = code.attributes || [];
  const allSpans = useSelector(
    (state: any) => spanSelectors.selectSpanAnnotations(state.gradeBenchPanel)
  ) as Accelerate.SpanAnnotation[];

  const caseAttributes = useSelector(
    (state: any) => state.gradeBenchPanel?.caseAttributes
  ) as CQA.Attribute[] | undefined;

  const rubricVersionId = useSelector(
    (state: any) => state.gradeBenchPanel?.qaRule?.data?.project_configuration?.rubricVersionId
  ) as number | undefined;

  const {
    evaluation,
    visibleQuestions,
    filteredAnswersMap,
    autofilledAnswersMap,
    isLoading,
    isSettled,
    error,
  } = useWorkflowEvaluation({
    workflowId: rubricWorkflow?.id,
    attributes,
    workflowQuestions: rubricWorkflow?.questions,
    codeContext: {
      original: code.original ?? null,
      code: code.code || '',
      sequenceNumber: code.sequenceNumber ?? 0,
      codeType: code.codeType || '',
    },
    workItemId,
    evaluateFn: evaluateFn!,
    enabled: !!evaluateFn && !!rubricWorkflow?.id,
    rubricVersionId,
    caseAttributes: caseAttributes || undefined,
  });

  const [lastChangedQuestionId, setLastChangedQuestionId] = useState<number | null>(null);

  // Use the shared hook for handling rubric answer changes with downstream cleanup
  const handleAnswerChangeWithCleanup = useHandleRubricAnswerChange(
    onAnswerChange,
    onDeleteAnswer,
    onClearDownstream
  );

  // Use a ref to break the circular dependency between hook and handler
  const handleAnswerChangeRef = useRef<(questionId: number, value: number | number[] | string) => void>(() => {});

  // Use the autofill hook to handle autofill logic (only for multiple-choice; FREE_TEXT has no autofill)
  const { trackAnswerChange, clearDownstreamTracking } = useRubricAutofill({
    entityId: code.id.toString(),
    entityKey: `${code.id}`,
    attributes,
    autofilledAnswers: autofilledAnswersMap,
    onAutofill: (questionId, answerValueId) => {
      handleAnswerChangeRef.current(questionId, answerValueId);
    }
  });

  // Handle answer change for a question. value: number = answerValueId (MULTIPLE_CHOICE/SYSTEM), number[] = spanAnnotationIds (SPAN/SPANS), string = answerDisplayText (FREE_TEXT)
  const handleAnswerChange = useCallback(
    async (questionId: number, value: number | number[] | string) => {
      if (isDisabled) return;
      if (!rubricWorkflow?.questions) return;

      setLastChangedQuestionId(questionId);

      const downstreamQuestionIds = getQuestionIdsToClear(
        visibleQuestions,
        attributes,
        questionId
      );

      const isFreeText = typeof value === 'string';
      if (!isFreeText && typeof value === 'number') {
        trackAnswerChange(questionId, value);
      }
      clearDownstreamTracking(downstreamQuestionIds);

      // Determine which answer field to use based on question type
      const question = visibleQuestions.find(wq => wq.question.id === questionId)?.question;
      const isSpan = question?.questionType === 'SPAN';
      const isSpans = question?.questionType === 'SPANS';

      let answerFields: Record<string, unknown>;
      if (isFreeText) {
        answerFields = { answerDisplayText: value };
      } else if (isSpan || isSpans) {
        answerFields = { spanAnnotationIds: value };
      } else {
        answerFields = { answerValueId: value };
      }

      await handleAnswerChangeWithCleanup({
        entityId: code.id.toString(),
        entityType: codeTypeToEntityType(code.codeType || 'procedure'),
        attributes,
        questionId,
        ...answerFields,
        downstreamQuestionIds
      });
    },
    [isDisabled, rubricWorkflow, attributes, visibleQuestions, code.id, code.codeType, handleAnswerChangeWithCleanup, trackAnswerChange, clearDownstreamTracking]
  );

  // Update the ref so the hook can call the handler
  handleAnswerChangeRef.current = handleAnswerChange;

  const onCompletionChangeRef = useRef(onCompletionChange);
  onCompletionChangeRef.current = onCompletionChange;

  const requiredComplete = useMemo(
    () => (isSettled ? isCodeComplete(visibleQuestions, attributes) : undefined),
    [isSettled, visibleQuestions, attributes]
  );

  useEffect(() => {
    if (requiredComplete === undefined) return;
    onCompletionChangeRef.current?.(requiredComplete);
  }, [requiredComplete]);

  if (error) {
    return (
      <Box pl={8} pr={4} py={3}>
        <Alert status="error" variant="left-accent" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Unable to load questions. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (isLoading && !evaluation) {
    return (
      <Box pl={8} pr={4} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
        <Center py={4}>
          <Spinner size="md" />
        </Center>
      </Box>
    );
  }

  if (visibleQuestions.length === 0) {
    return null;
  }

  return (
    <Box
      className="code-questions-section"
      pl={8}
      pr={4}
      py={3}
      bg="gray.50"
      borderTop="1px solid"
      borderColor="gray.200"
    >
      {isCaseIncomplete && (
        <Alert status="info" variant="left-accent" borderRadius="md" mb={2} sx={{ display: 'flex !important', flexDirection: 'row !important', alignItems: 'center !important' }}>
          <AlertIcon mr={2} flexShrink={0} />
          <AlertDescription fontSize="sm">
            Please complete all case-level questions before answering code questions.
          </AlertDescription>
        </Alert>
      )}
      <WorkflowWizard
        visibleQuestions={visibleQuestions}
        attributes={attributes}
        onAnswerChange={handleAnswerChange}
        isDisabled={isDisabled}
        isLoading={isLoading}
        autofilledAnswersMap={autofilledAnswersMap}
        filteredAnswersMap={filteredAnswersMap}
        spans={allSpans}
        lastChangedQuestionId={lastChangedQuestionId}
        resetRef={resetRef}
      />
    </Box>
  );
};

export default CodeQuestionsSection;
